import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import RwandaNewsHeader from './RwandaNewsHeader';
import YourBriefingSection from './YourBriefingSection';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import MainContentGrid from './MainContentGrid';
import AnnouncementsSection from './AnnouncementsSection';
import CustomizeModal from './CustomizeModal';
import ReaderPanel from './ReaderPanel';
import FollowingPage from './FollowingPage';
import LocalPage from './LocalPage';
import LibraryPage from './LibraryPage';
import VideosPage from './VideosPage';
import AuthModal from './AuthModal';
import SettingsModal from './SettingsModal';
import HelpModal from './HelpModal';
import Footer from './Footer';
import Pagination from './Pagination';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import useSwipeNavigation from '../hooks/useSwipeNavigation';
import { useLanguage } from '../contexts/LanguageContext';
import { Film, Trophy, Sparkles, Gamepad2, Music, Cpu, Heart, Globe } from 'lucide-react';

// Lazy load heavy components (72KB+ each)
const AdminPanel = lazy(() => import('./AdminPanel'));
const CreatorDashboard = lazy(() => import('./CreatorDashboard'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== Client-Side Cache ====================
const CACHE_TTL = 60 * 1000; // 1 minute cache (reduced for fresher content)
const apiCache = new Map();

const getCachedData = (key) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  apiCache.delete(key);
  return null;
};

const setCachedData = (key, data) => {
  // Only cache if data is valid (not empty arrays for critical endpoints)
  if (Array.isArray(data) && data.length === 0) {
    return; // Don't cache empty results
  }
  apiCache.set(key, { data, timestamp: Date.now() });
};

// Clear cache function (useful for forcing refresh)
const clearApiCache = () => {
  apiCache.clear();
};

// Cached axios get with smart caching - NO auth headers for public endpoints
const cachedGet = async (url) => {
  const cached = getCachedData(url);
  if (cached) return { data: cached };
  
  // Create a fresh axios instance without auth headers for public data fetching
  const response = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      // Explicitly don't send Authorization header for public endpoints
    },
    transformRequest: [(data, headers) => {
      // Remove Authorization header for public requests
      delete headers['Authorization'];
      return data;
    }]
  });
  setCachedData(url, response.data);
  return response;
};

// Loading spinner for lazy components
const LazyLoader = () => (
  <div className="flex items-center justify-center p-8">
    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Topic to API category mapping
const TOPIC_TO_CATEGORY = {
  'World': 'world',
  'Technology': 'technology',
  'Business': 'business',
  'Entertainment': 'entertainment',
  'Sports': 'sports',
  'Health': 'health',
  'Science': 'science',
  'Politics': 'world',
  'Food': 'entertainment',
  'Gaming': 'technology',
  'Finance': 'business',
  'Digital currencies': 'business',
  'Space': 'science',
  'Africa': 'africa',
};

// Default topics for the user
const DEFAULT_TOPICS = ['World', 'Technology', 'Business', 'Sports', 'Entertainment', 'Health'];

// Swipe navigation categories
const SWIPE_CATEGORIES = [
  { id: 'home', label: 'Home' },
  { id: 'videos', label: 'Videos' },
  { id: 'library', label: 'Library' },
  { id: 'local', label: 'Local' },
];

// Persistent and in-memory caches to completely eliminate flashing on initial open and reopen
const FEED_CACHE_KEY = 'yego_home_feed_cache_v2';
const loadFeedCache = () => {
  try {
    const raw = localStorage.getItem(FEED_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.stories) && parsed.stories.length > 0) {
        return parsed;
      }
    }
  } catch (e) {}
  return null;
};

const initialFeedCache = loadFeedCache();
let homeStoriesCache = initialFeedCache 
  ? { articles: initialFeedCache.stories, total: initialFeedCache.total || initialFeedCache.stories.length } 
  : null;
let homeAnnouncementsCache = initialFeedCache?.announcements || null;
let homeVideosCache = initialFeedCache?.videos || null;
let homeCustomSectionsCache = initialFeedCache?.customSections || null;

const saveFeedCache = (stories, total, announcements, videos, customSections) => {
  try {
    localStorage.setItem(FEED_CACHE_KEY, JSON.stringify({
      stories: (stories || []).slice(0, 40),
      total: total || 0,
      announcements: announcements || [],
      videos: videos || [],
      customSections: customSections || [],
      updatedAt: Date.now()
    }));
  } catch (e) {}
};

const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

const RwandaNewsLayout = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('home');
  const [visitedCategories, setVisitedCategories] = useState(() => new Set(['home']));

  useEffect(() => {
    if (activeCategory) {
      setVisitedCategories(prev => {
        if (prev.has(activeCategory)) return prev;
        const next = new Set(prev);
        next.add(activeCategory);
        return next;
      });
    }
  }, [activeCategory]);

  const { darkMode, setDarkMode } = useTheme();
  const [loading, setLoading] = useState(!homeStoriesCache);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [readerOpen, setReaderOpen] = useState(false);
  const closeTimerRef = useRef(null);
  
  // Prevent background scroll and double scrollbar when admin modal is open
  useEffect(() => {
    if (showAdminPanel) {
      const origHtml = document.documentElement.style.overflow;
      const origBody = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      return () => {
        document.documentElement.style.overflow = origHtml;
        document.body.style.overflow = origBody;
      };
    }
  }, [showAdminPanel]);
  
  // Toast state
  const [toast, setToast] = useState(null);
  
  const { user, isAuthenticated, isAdmin, canWrite, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  
  // Handler for opening the fullscreen article editor
  const handleOpenCreator = () => {
    if (isAuthenticated && canWrite) {
      // User is logged in and has write permission - go to editor
      navigate('/write');
    } else if (isAuthenticated && !canWrite) {
      // User is logged in but not a contributor - show toast and redirect to request access
      showToast('Please request contributor access to write articles');
      navigate('/write'); // This will show the request access view in the editor
    } else {
      // User is not logged in - open auth modal first
      setShowAuthModal(true);
    }
  };
  
  // Swipe navigation disabled - users can use the navigation menu instead
  // const { handlers: swipeHandlers, currentIndex, totalCategories, swipeDirection } = useSwipeNavigation(
  //   SWIPE_CATEGORIES,
  //   activeCategory,
  //   setActiveCategory,
  //   { threshold: 75, allowedTime: 400 }
  // );
  
  // Show toast helper
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 5000);
  };
  
  // Settings state
  const [hideImages, setHideImages] = useState(() => {
    const saved = localStorage.getItem('yegoNewsSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.showImages === false;
    }
    return false;
  });
  
  // Homepage settings - whether to show fetched/RSS articles
  const [showFetchedArticles, setShowFetchedArticles] = useState(true);
  
  // Fetch homepage settings on mount
  useEffect(() => {
    const fetchHomepageSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/homepage`);
        setShowFetchedArticles(response.data.show_fetched_articles !== false);
      } catch (error) {
        // Default to showing fetched articles if API fails
        setShowFetchedArticles(true);
      }
    };
    fetchHomepageSettings();
  }, []);

  // Listen for settings changes
  useEffect(() => {
    const handleSettingsChange = (event) => {
      const newSettings = event.detail;
      setHideImages(newSettings.showImages === false);
    };
    
    window.addEventListener('yegoNewsSettingsChange', handleSettingsChange);
    return () => window.removeEventListener('yegoNewsSettingsChange', handleSettingsChange);
  }, []);
  
  // Listen for article switching from ReaderPanel
  useEffect(() => {
    const handleSwitchArticle = (event) => {
      const newArticle = event.detail;
      if (newArticle) {
        const isMob = typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false;
        if (!isMob) {
          const title = newArticle.title_en || newArticle.title || newArticle.title_rw || '';
          const slug = generateSlug(title);
          if (slug) navigate(`/news/${slug}`);
          return;
        }
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setSelectedArticle(newArticle);
        const title = newArticle.title_en || newArticle.title || newArticle.title_rw || '';
        const slug = generateSlug(title);
        const mobileUrl = slug ? `/?article=${slug}` : '/';
        if (slug && typeof window !== 'undefined') {
          window.history.replaceState({ readerOpen: true, layer: 1, articleId: newArticle.id }, '', mobileUrl);
        }
        // Scroll the reader panel content to top
        setTimeout(() => {
          const contentDiv = document.querySelector('.reader-content');
          if (contentDiv) contentDiv.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    };
    
    window.addEventListener('switchArticle', handleSwitchArticle);
    return () => window.removeEventListener('switchArticle', handleSwitchArticle);
  }, [navigate]);
  
  // Data states
  const [myStories, setMyStories] = useState(() => homeStoriesCache?.articles || []);
  const [totalArticles, setTotalArticles] = useState(() => homeStoriesCache?.total || 0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rwandaNews, setRwandaNews] = useState([]);
  const [sectionsData, setSectionsData] = useState({});
  const [customSections, setCustomSections] = useState(() => homeCustomSectionsCache || []);
  const [announcementsList, setAnnouncementsList] = useState(() => homeAnnouncementsCache || []);
  const [videoArticles, setVideoArticles] = useState(() => homeVideosCache || []);
  const [overflowArticles, setOverflowArticles] = useState([]); // Articles that overflow from left sidebar
  const [userTopics, setUserTopics] = useState(() => {
    const saved = localStorage.getItem('yegoNewsTopics');
    return saved ? JSON.parse(saved) : DEFAULT_TOPICS;
  });
  
  // Pagination config - Many articles per page before needing page 2
  const ARTICLES_PER_PAGE = 40;

  const fetchData = useCallback(async (page = 1) => {
    try {
      // Fetch articles with pagination (no cache for paginated requests)
      const myStoriesRes = await axios.get(`${API}/my-stories?page=${page}&limit=${ARTICLES_PER_PAGE}`).catch(() => ({ data: { articles: [], total: 0 } }));
      
      // Fetch other data with cache
      const [announcementsRes, videosRes, customSectionsRes] = await Promise.all([
        cachedGet(`${API}/announcements`).catch(() => ({ data: [] })),
        cachedGet(`${API}/videos/homepage?limit=8`).catch(() => ({ data: [] })),
        cachedGet(`${API}/custom-sections`).catch(() => ({ data: [] })),
      ]);

      // Handle paginated response
      const storiesData = myStoriesRes.data;
      let fetchedArticles = [];
      let fetchedTotal = 0;
      if (storiesData && storiesData.articles) {
        fetchedArticles = storiesData.articles;
        fetchedTotal = storiesData.total || 0;
      } else if (Array.isArray(storiesData)) {
        fetchedArticles = storiesData;
        fetchedTotal = storiesData.length;
      }
      
      if (fetchedArticles.length > 0) {
        setMyStories(fetchedArticles);
        setTotalArticles(fetchedTotal);
      }
      
      setRwandaNews([]);
      const annList = announcementsRes.data || [];
      const vidList = videosRes.data || [];
      const custSec = customSectionsRes.data || [];
      setAnnouncementsList(annList);
      setVideoArticles(vidList);
      setCustomSections(custSec);

      if (page === 1 && fetchedArticles.length > 0) {
        homeStoriesCache = { articles: fetchedArticles, total: fetchedTotal };
        homeAnnouncementsCache = annList;
        homeVideosCache = vidList;
        homeCustomSectionsCache = custSec;
        saveFeedCache(fetchedArticles, fetchedTotal, annList, vidList, custSec);
      }
      setSectionsData({});
      setLoading(false);
      
      // Scroll to top when changing pages (not on initial load)
      if (page > 1) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [ARTICLES_PER_PAGE]);

  useEffect(() => {
    // Fetch data for current page - fetchData is a useCallback that handles state updates
    fetchData(currentPage);
  }, [fetchData, currentPage]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setLoading(true);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalArticles / ARTICLES_PER_PAGE);

  const handleArticleClick = (article) => {
    const isMob = typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false;
    const title = article.title_en || article.title || article.title_rw || '';
    const slug = generateSlug(title);
    const targetPath = slug ? `/news/${slug}` : '/';

    if (!isMob) {
      // On web/desktop: open the full page directly for search indexing
      navigate(targetPath);
      return;
    }

    // On mobile: open the reader panel sliding in from the right edge
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setSelectedArticle(article);
    setReaderOpen(true);
    if (slug) {
      const mobileUrl = `/?article=${slug}`;
      if (window.location.search !== `?article=${slug}`) {
        window.history.pushState({ readerOpen: true, layer: 1, articleId: article.id }, '', mobileUrl);
      }
    }
  };

  const handleCloseReader = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setReaderOpen(false);
    closeTimerRef.current = setTimeout(() => {
      setSelectedArticle(null);
      closeTimerRef.current = null;
    }, 260);
    if (window.history.state?.layer >= 1) {
      window.history.back();
    } else if (window.location.search.includes('article=')) {
      window.history.replaceState(null, '', '/');
    }
  };

  // Restore article from query param on mobile refresh
  useEffect(() => {
    const isMob = typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false;
    if (!isMob) return;

    const searchParams = new URLSearchParams(window.location.search);
    const articleSlug = searchParams.get('article');
    if (articleSlug) {
      axios.get(`${API}/articles/slug/${articleSlug}`)
        .then(res => {
          if (res.data) {
            setSelectedArticle(res.data);
            setReaderOpen(true);
            if (!window.history.state?.readerOpen) {
              window.history.replaceState({ readerOpen: true, layer: 1, articleId: res.data.id }, '', `/?article=${articleSlug}`);
            }
          }
        })
        .catch(err => {
          console.warn('Could not restore article from url query:', err);
        });
    }
  }, []);

  // Listen for browser/system back button while reader is open
  useEffect(() => {
    const handlePopState = (event) => {
      const targetLayer = event.state?.layer || 0;
      // If we are at layer 1 or above (authorPanel or nestedReader is open), DO NOT close reader panel!
      if (targetLayer >= 1) return;

      if (readerOpen) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setReaderOpen(false);
        closeTimerRef.current = setTimeout(() => {
          setSelectedArticle(null);
          closeTimerRef.current = null;
        }, 260);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [readerOpen]);

  // Listen for openArticleComments event (from context menu)
  useEffect(() => {
    const handleOpenArticleComments = (e) => {
      const { article, scrollToComments } = e.detail;
      if (article) {
        const isMob = typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false;
        const title = article.title_en || article.title || article.title_rw || '';
        const slug = generateSlug(title);
        const targetPath = slug ? `/news/${slug}` : '/';

        if (!isMob) {
          navigate(targetPath);
          return;
        }

        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setSelectedArticle(article);
        setReaderOpen(true);
        // Scroll to comments after panel opens
        if (scrollToComments) {
          setTimeout(() => {
            const commentsSection = document.getElementById('comments-section');
            if (commentsSection) {
              commentsSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 500);
        }
      }
    };

    window.addEventListener('openArticleComments', handleOpenArticleComments);
    return () => window.removeEventListener('openArticleComments', handleOpenArticleComments);
  }, [navigate]);

  const handleArticleCreated = (article) => {
    setMyStories(prev => [article, ...prev]);
    // Note: With fullscreen editor, user is redirected back to home after publishing
  };

  const handleSaveTopics = (topics) => {
    setUserTopics(topics);
    localStorage.setItem('yegoNewsTopics', JSON.stringify(topics));
  };

  // Prepare topics with articles for YourTopicsSection
  const topicsWithArticles = userTopics.map(topic => ({
    name: topic,
    articles: sectionsData[TOPIC_TO_CATEGORY[topic] || 'world'] || [],
  }));

  // For Library, Following, Local, and Videos pages - don't show main loading skeleton
  // They have their own loading states
  const isSpecialPage = ['library', 'following', 'local', 'videos'].includes(activeCategory);

  // Loading skeleton (only for main homepage) - matches newspaper layout
  if (loading && !isSpecialPage) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950">
        <RwandaNewsHeader
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          onOpenCreator={handleOpenCreator}
          onOpenAuth={() => setShowAuthModal(true)}
          onOpenAdmin={() => navigate('/admin')}
          onOpenSettings={() => setShowSettingsModal(true)}
          onOpenHelp={() => setShowHelpModal(true)}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
        />
        <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
          {/* Your briefing skeleton */}
          <div className="mb-4 sm:mb-8">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="h-7 sm:h-9 bg-gray-200 dark:bg-gray-800 rounded w-40 sm:w-48 mb-2 animate-pulse"></div>
                <div className="h-4 sm:h-5 bg-gray-200 dark:bg-gray-800 rounded w-32 sm:w-40 animate-pulse"></div>
              </div>
              <div className="h-12 w-24 sm:h-16 sm:w-32 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
            </div>
          </div>
          
          {/* Main Feed Container - Unboxed on mobile */}
          <div className="bg-transparent md:bg-white dark:bg-transparent md:dark:bg-gray-900 border-0 md:border md:border-gray-200 md:dark:border-gray-800 p-0 md:p-6 shadow-none md:shadow-sm">
            <div className="flex gap-4 md:gap-6">
              {/* Left sidebar skeleton - hidden on mobile */}
              <aside className="hidden lg:block w-72 xl:w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 pr-6">
                <div className="sticky top-20">
                  <div className="pb-3 mb-3 border-b-2 border-gray-300">
                    <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-32 animate-pulse"></div>
                  </div>
                  <div className="space-y-0">
                    {[1,2,3,4,5,6,7].map(i => (
                      <div key={i} className="flex gap-3 py-3 border-b border-gray-100 dark:border-gray-800 animate-pulse">
                        <div className="w-8 h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
                        </div>
                        <div className="w-14 h-14 bg-gray-200 dark:bg-gray-800"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
              
              {/* Main content skeleton */}
              <div className="flex-1 min-w-0 animate-pulse">
                {/* Hero section - 2 featured articles */}
                <div className="mb-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6 pb-4 md:pb-6 border-b border-gray-200 dark:border-gray-800">
                    {[1, 2].map(i => (
                      <div key={i} className="flex flex-col">
                        <div className="aspect-[16/10] bg-gray-200 dark:bg-gray-800"></div>
                        <div className="pt-2 sm:pt-3 space-y-2">
                          <div className="h-4 sm:h-5 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
                          <div className="h-4 sm:h-5 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-24 mt-2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* More Stories section */}
                <div className="my-8">
                  <div className="hidden md:block mb-4 pb-2 border-b-2 border-gray-300">
                    <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-28"></div>
                  </div>
                  <div className="md:hidden h-6 bg-gray-200 dark:bg-gray-800 rounded w-32 mb-4"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {[1,2,3,4,5,6].map(i => (
                      <div key={i} className="flex flex-col">
                        <div className="aspect-[16/10] bg-gray-200 dark:bg-gray-800 rounded-lg md:rounded-none"></div>
                        <div className="pt-2 space-y-2">
                          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-2/3"></div>
                          <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-20"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Right sidebar skeleton - hidden on mobile */}
              <aside className="hidden xl:block w-72 flex-shrink-0">
                <div className="sticky top-20">
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-24 mb-4 animate-pulse"></div>
                  <div className="h-48 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"></div>
                </div>
              </aside>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950" style={{ overflowX: 'clip' }}>
      <RwandaNewsHeader
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        onOpenCreator={handleOpenCreator}
        onOpenAuth={() => setShowAuthModal(true)}
        onOpenAdmin={() => navigate('/admin')}
        onOpenSettings={() => setShowSettingsModal(true)}
        onOpenHelp={() => setShowHelpModal(true)}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />

      <main 
        className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8"
      >
        {/* Render different views based on activeCategory - preserved in DOM for instant, smooth switching */}
        <div className={activeCategory === 'following' ? 'tab-panel-active' : 'tab-panel-hidden'}>
          {visitedCategories.has('following') && (
            <FollowingPage onArticleClick={handleArticleClick} />
          )}
        </div>

        <div className={activeCategory === 'local' ? 'tab-panel-active' : 'tab-panel-hidden'}>
          {visitedCategories.has('local') && (
            <LocalPage onArticleClick={handleArticleClick} />
          )}
        </div>

        <div className={activeCategory === 'library' ? 'tab-panel-active' : 'tab-panel-hidden'}>
          {visitedCategories.has('library') && (
            <LibraryPage 
              onBack={() => setActiveCategory('home')} 
              onArticleClick={handleArticleClick}
              darkMode={darkMode}
            />
          )}
        </div>

        <div className={activeCategory === 'videos' ? 'tab-panel-active' : 'tab-panel-hidden'}>
          {visitedCategories.has('videos') && (
            <VideosPage />
          )}
        </div>

        <div className={activeCategory === 'home' ? 'tab-panel-active' : 'tab-panel-hidden'}>
          {/* Your Briefing - Date and Weather */}
          <div>
            <YourBriefingSection />
          </div>

          {/* Main Feed Container - Unboxed on mobile, clean vertical flow */}
          <div className="bg-transparent md:bg-white dark:bg-transparent md:dark:bg-gray-900 border-0 md:border md:border-gray-200 md:dark:border-gray-800 p-0 md:p-6 mt-3 md:mt-6 shadow-none md:shadow-sm">
            <div className="flex gap-4 md:gap-6">
              {/* Left Sidebar - Recent Stories (hidden on mobile) */}
              <LeftSidebar 
                articles={myStories} 
                onArticleClick={handleArticleClick}
                onOverflowArticles={setOverflowArticles}
              />
              
              {/* Main Content Grid - Mixed articles with ads */}
              <div className="flex-1 min-w-0">
                <MainContentGrid 
                  featuredArticles={myStories}
                  overflowArticles={overflowArticles}
                  categoryData={{
                    entertainment: sectionsData.entertainment || [],
                    sports: sectionsData.sports || [],
                    technology: sectionsData.technology || [],
                    business: sectionsData.business || [],
                    world: sectionsData.world || [],
                  }}
                  customSections={customSections}
                  announcements={announcementsList}
                  videoArticles={videoArticles}
                  onArticleClick={handleArticleClick}
                />
                
                {/* Announcements & Partners Section - Aligned with main content */}
                <AnnouncementsSection />
              </div>
              
              {/* Right Sidebar - Ads and Partners (hidden on mobile) */}
              <RightSidebar />
            </div>
            
            {/* Pagination - At the very bottom, full width (always show) */}
            <div className="mt-8 pb-8 border-t border-gray-200 dark:border-gray-800">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages || 1}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Customize Modal */}
      <CustomizeModal
        isOpen={showCustomize}
        onClose={() => setShowCustomize(false)}
        onSave={handleSaveTopics}
        initialTopics={userTopics}
      />

      {/* Reader Panel */}
      <ReaderPanel
        article={selectedArticle}
        isOpen={readerOpen}
        onClose={handleCloseReader}
        onOpenAuth={() => setShowAuthModal(true)}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      {/* Admin Panel - Lazy loaded */}
      {showAdminPanel && isAdmin && (
        <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-950 overflow-y-scroll" style={{ scrollbarGutter: 'stable' }}>
          <div className="max-w-7xl mx-auto px-4 py-8">
            <Suspense fallback={<LazyLoader />}>
              <AdminPanel onBack={() => setShowAdminPanel(false)} />
            </Suspense>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 max-w-md">
            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium">{toast}</p>
            <button 
              onClick={() => setToast(null)}
              className="ml-2 text-gray-400 hover:text-white dark:hover:text-gray-900"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RwandaNewsLayout;
